import {
  BarcodeBatch,
  BarcodeBatchBasicOverlay,
  BarcodeBatchBasicOverlayStyle,
  BarcodeBatchSettings,
  BarcodeCapture,
  BarcodeCaptureOverlay,
  BarcodeCaptureSettings,
} from '@scandit/web-datacapture-barcode'
import {
  DataCaptureContext,
  DataCaptureView,
  Logger,
  type ModuleLoader,
  RectangularViewfinder,
  RectangularViewfinderLineStyle,
  RectangularViewfinderStyle,
} from '@scandit/web-datacapture-core'
import { LabelCapture, LabelCaptureSettingsBuilder, type LabelCaptureSettings } from '@scandit/web-datacapture-label'

function getLicense() {
  if (!window.WebsdkDemos) {
    throw new Error(
      'license key could not be loaded. Please check that the license key is valid and has not expired. You can also try to reload the page with ?license=...'
    )
  }
  return (
    window.WebsdkDemos.getLicenseKeyByHostname(window.location.hostname, true) ||
    import.meta.env.VITE_SCANDIT_LICENSE_KEY
  )
}

export class SDK {
  barcodeCaptureSettings: BarcodeCaptureSettings | undefined
  barcodeCaptureMode: BarcodeCapture | undefined
  barcodeBatchMode: BarcodeBatch | undefined
  barcodeBatchSettings: BarcodeBatchSettings | undefined
  labelCaptureMode: LabelCapture | undefined
  view?: DataCaptureView

  public async configure(moduleLoaders: ModuleLoader[]) {
    this.view = new DataCaptureView()
    this.view.connectToElement(document.getElementById('data-capture-view')!)
    const license = getLicense()
    await DataCaptureContext.forLicenseKey(license, {
      libraryLocation: new URL('library/engine', document.baseURI).toString(),
      moduleLoaders,
      logLevel: Logger.Level.Debug,
    })
    await this.view.setContext(DataCaptureContext.sharedInstance)
    return this
  }

  public async configureBatchMode(): Promise<{ mode: BarcodeBatch; settings: BarcodeBatchSettings }> {
    await DataCaptureContext.sharedInstance?.removeAllModes()
    this.barcodeBatchSettings = new BarcodeBatchSettings()
    this.barcodeBatchMode = await BarcodeBatch.forContext(DataCaptureContext.sharedInstance!, this.barcodeBatchSettings)
    await this.barcodeBatchMode.setEnabled(false)
    await BarcodeBatchBasicOverlay.withBarcodeBatchForViewWithStyle(
      this.barcodeBatchMode,
      this.view,
      BarcodeBatchBasicOverlayStyle.Frame
    )
    return { mode: this.barcodeBatchMode, settings: this.barcodeBatchSettings }
  }

  public async configureCaptureMode(): Promise<{ mode: BarcodeCapture; settings: BarcodeCaptureSettings }> {
    await DataCaptureContext.sharedInstance?.removeAllModes()

    this.barcodeCaptureSettings = new BarcodeCaptureSettings()
    this.barcodeCaptureMode = await BarcodeCapture.forContext(
      DataCaptureContext.sharedInstance!,
      this.barcodeCaptureSettings
    )
    await this.barcodeCaptureMode.setEnabled(false)

    const barcodeCaptureOverlay: BarcodeCaptureOverlay = await BarcodeCaptureOverlay.withBarcodeCaptureForView(
      this.barcodeCaptureMode,
      this.view!
    )

    const viewFinder: RectangularViewfinder = new RectangularViewfinder(
      RectangularViewfinderStyle.Square,
      RectangularViewfinderLineStyle.Light
    )

    await barcodeCaptureOverlay.setViewfinder(viewFinder)
    return { mode: this.barcodeCaptureMode, settings: this.barcodeCaptureSettings }
  }

  public async configureLabelCaptureMode(settings: LabelCaptureSettings): Promise<{
    mode: LabelCapture
  }> {
    await DataCaptureContext.sharedInstance?.removeAllModes()

    // Create with EMPTY settings first, then applySettings. This mirrors the
    // debug app pattern (LabelCaptureModeViewModel.initialize + applyLabelCaptureSettings).
    // `LabelCapture.forContext(ctx, settingsWithDedicatedFields)` does not properly
    // initialize the OCR text recognizer required by IMEI/Serial/dedicated barcode
    // fields; `applySettings()` does. Empty-init + applySettings is the only path
    // that makes the SmartphoneBox scenario work.
    //
    // IMPORTANT: this pattern is required ONLY here, on initial mode creation via
    // forContext. Subsequent runtime updates (e.g. preset switches via
    // LabelCaptureSettingsStore.endEditing) should call `mode.applySettings(settings)`
    // directly on the already-initialized mode; recreating the mode there would
    // tear down the OCR machinery and re-introduce the bug.
    const emptySettings = await new LabelCaptureSettingsBuilder().build()
    this.labelCaptureMode = await LabelCapture.forContext(DataCaptureContext.sharedInstance!, emptySettings)
    await this.labelCaptureMode.applySettings(settings)
    await this.labelCaptureMode.setEnabled(false)

    return { mode: this.labelCaptureMode }
  }

  public getCurrentMode(): BarcodeCapture | BarcodeBatch | LabelCapture | undefined {
    if (this.barcodeCaptureMode != null) {
      return this.barcodeCaptureMode
    }
    if (this.barcodeBatchMode != null) {
      return this.barcodeBatchMode
    }
    if (this.labelCaptureMode != null) {
      return this.labelCaptureMode
    }
  }
}

export default new SDK()
